import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import { store } from 'redux/store';

import { Board, BoardVideo } from 'types';
import asyncActionSwal from 'utils/asyncActionSwal';
import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';

import { setModal } from 'modules/_shared/redux/modalActions';
import { updateBoard } from 'modules/boards/redux/boardActions';
import { useGetBoardsSectionPermissions } from 'modules/boards/hooks/useGetBoardsSectionPermissions';
import CustomLoading from 'modules/_shared/components/CustomLoading';

import EmptyStateCard from 'modules/_shared/components/EmptyStates/EmptyStateCard';
import AddVideoModal from '../../modals/AddVideoModal';
import ViewVideoModal from './components/ViewVideoModal';
import RenameVideoModal from './components/RenameVideoModal';
import VideosHeader from './components/VideosHeader';
import VideosList from './components/VideosList';

type BoardVideosProps = {
  board: Board;
};

const BoardVideos: FC<BoardVideosProps> = ({ board }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const { isAdmin, isReadOnly } = useGetBoardsSectionPermissions().data;

  const [isLoading, setIsLoading] = useState(false);

  const videos: BoardVideo[] = board?.videos || [];

  const handleAddVideo = () => {
    dispatch(
      setModal(<AddVideoModal boardId={board._id} currentVideos={videos} />)
    );
  };

  const handleViewVideo = (videoId: string) => {
    const video = videos.find((video) => video.videoId === videoId);
    if (video) {
      dispatch(setModal(<ViewVideoModal video={video} />));
    }
  };

  const handleRenameVideo = (videoId: string) => {
    const video = videos.find((video) => video.videoId === videoId);
    const videoIndex = videos.findIndex((video) => video.videoId === videoId);

    if (!video) return;

    dispatch(
      setModal(
        <RenameVideoModal
          boardId={board._id}
          currentVideos={videos}
          videoIndex={videoIndex}
        />
      )
    );
  };

  const handleDeleteVideo = async (videoId: string) => {
    const video = videos.find((video) => video.videoId === videoId);
    const videoIndex = videos.findIndex((video) => video.videoId === videoId);

    if (!video) return;

    asyncActionSwal({
      swalTitle: t('AreYouSureAboutDelVideo') || 'Are you sure?',
      swalElementName: video.title,
      swalInfoText:
        t('ThisProcedureCantUndo') || 'This procedure cannot be undone',
      swalConfirmButtonText: t('OkDelete') || 'Delete',
      callback: async () => {
        try {
          setIsLoading(true);
          const updatedVideos = videos.filter(
            (_: unknown, i: number) => i !== videoIndex
          );
          await store.dispatch(
            updateBoard(board._id, { videos: updatedVideos }, false)
          );
          successSwal(
            t('VideoDeletedSuccessfully') || 'Video deleted successfully'
          );
        } catch (error) {
          errorSwal(t('ErrorDeletingVideo') || 'Error deleting video');
        } finally {
          setIsLoading(false);
        }
      },
    });
  };

  return (
    <>
      <div className="nk-content-body pt-4">
        <div className="nk-content-wrap">
          <div className="nk-block-head">
            <div className="nk-block-between justify-content-between">
              <h6 style={{ marginTop: -7 }}>
                {t('Multimedia') || 'Multimedia'}
                <span className="badge badge-dim badge-light ml-2">BETA</span>
              </h6>

              {isAdmin && (
                <div className="nk-block-head-content">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAddVideo}
                    disabled={isReadOnly}
                  >
                    <em className="icon ni ni-plus mr-1" />
                    {t('AddVideo') || 'Add Video'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {videos.length > 0 && (
            <div className="nk-block mb-5">
              <div className="card rounded-xl">
                <div className="nk-files nk-files-view-list">
                  <VideosHeader hasOptions isAdminView={isAdmin} />
                  <VideosList
                    videos={videos}
                    hasOptions
                    isAdminView={isAdmin}
                    onViewVideo={handleViewVideo}
                    onRenameVideo={isAdmin ? handleRenameVideo : undefined}
                    onDeleteVideo={isAdmin ? handleDeleteVideo : undefined}
                    textSize={400}
                  />
                </div>
              </div>
            </div>
          )}

          {videos.length === 0 && (
            <EmptyStateCard
              mainMessage={t('NoVideosAvailable')}
              infoMessage={t('NoVideosAvailableDescription')}
            />
          )}
        </div>
      </div>
      {isLoading && <CustomLoading />}
    </>
  );
};

export default BoardVideos;
